import React from 'react';
import OeTip from './OeTip';
import BestandTooltip, {werteAusKennzahlen} from '../Stock/BestandTooltip';
import {fmt0, qtyNum} from './format';
import {availCat, availOf, isExcluded, physOf, pickedOf, sourceWarehouseFor, stammLagerOf} from './availability';
import {WH_LAHR, bookQtyFor, checkKeyOf, massCand, stockCorrectionFor} from './wizard';

// Bestandsaktionen in der Positionstabelle (Original `stockActionBtn` 1397,
// `stockCheckBtn` 3820, `massAvailButtons` 3690). Sie erscheinen nur im
// Kommissionierungs-Schritt (6) und nur für Positionen, die ab Lahr laufen —
// Schneider und Cosmos melden ihren Bestand selbst (alle 15 Minuten per API).
//
// ABGLEICH E67, E103, E112, E115, G27, H21, J56.

const SPERRE_TIP = 'Lahr-Bestand in Pflege — Reservierungen erst nach Freigabe';

/** Aktionsknöpfe einer Position (Original `stockActionBtn`, 1397). */
export function StockActionButtons({row, index, stock, order, kitParent, lahrGesperrt, canWarehouse, onBook, onUnpick, onPosOut}) {
    if (row._new || !row.articleId) {
        return null;
    }

    if (kitParent) {
        return (
            <OeTip tip="Set-Artikel: ein eigener Bestand wird nicht geführt — gebucht wird auf den Unterpositionen (4.1, 4.2 …).">
                <span className="muted" style={{fontSize: 11}}>Set – auf Unterpositionen buchen</span>
            </OeTip>
        );
    }

    if (isExcluded(row)) {
        return (
            <OeTip tip={'Diese Position wird nicht geliefert („Keine") — im Lager ist dafür nichts zu tun.'}>
                <span className="muted" style={{fontSize: 11}}>nicht geliefert</span>
            </OeTip>
        );
    }

    if (sourceWarehouseFor(row, stock, order) !== WH_LAHR) {
        const sl = stammLagerOf(row, stock);
        const wl = sl === 'SNV' ? 'Schneider' : (sl || 'anderem Lager');
        return (
            <OeTip
                tip={`Diese Position wird ab ${wl} geliefert — hier ist nichts zu buchen, reserviert wird im Hintergrund von selbst.`}
                detail={'Schneider (SNV) und Cosmos melden ihren Bestand selbst (alle 15 Min. per API), darauf haben wir keinen Einfluss. '
                    + 'Reserviert wird bei Schneider über den Lieferschein „Neu", bei Cosmos per Entnahme am Auftragslager. '
                    + 'Den Stand zeigt die Marke „🔒 reserviert" neben der Ampel.'}
            >
                <span className="muted" style={{fontSize: 11}}>ab {wl}</span>
            </OeTip>
        );
    }

    const gesperrt = lahrGesperrt || !canWarehouse;
    const sperrTip = lahrGesperrt ? SPERRE_TIP : 'Zum Buchen und Reservieren fehlt das Recht „Lager".';

    const need = qtyNum(row.quantity) - qtyNum(row.shippedQuantity);
    const phys = physOf(row, stock, order) || 0;
    const picked = pickedOf(row);

    const pickBtn = (
        <OeTip
            tip={gesperrt ? sperrTip : 'Fall 1 – überbucht: verfügbaren Bestand per Entnahme für DIESEN Auftrag reservieren; macht die Position lieferbar.'}
            detail={gesperrt ? undefined : 'Reserviert höchstens Lagerbestand minus von anderen Aufträgen reserviert. Ändert den Lagerbestand NICHT. Umkehrbar über „↩ aufheben".'}
        >
            <button
                type="button"
                className="stkbtn res"
                disabled={gesperrt}
                onClick={() => onBook(index, 'pick')}
            >
                🔒 reservieren
            </button>
        </OeTip>
    );

    const raiseBtn = (
        <OeTip
            tip={gesperrt ? sperrTip : 'Fall 3 – verfügbar ist 0, physisch ist aber Ware da: Bestand per Wareneingang ohne Bezug erhöhen.'}
            detail={gesperrt ? undefined : 'Echte Bestandskorrektur, erscheint im Wochenreport; im Testmodus Trockenlauf.'}
        >
            <button
                type="button"
                className="stkbtn ok"
                disabled={gesperrt}
                onClick={() => onBook(index, 'in')}
            >
                ✓ doch da (Bestand +)
            </button>
        </OeTip>
    );

    const lowerBtn = (
        <OeTip
            tip={gesperrt ? sperrTip : 'Fall 2 – nicht da: Lagerbestand senken, höchstens bis auf die von anderen Aufträgen reservierte Menge.'}
            detail={gesperrt ? undefined : 'Untergrenze = fremde Reservierungen, sie bleiben gedeckt; dieser Auftrag geht leer aus. Echte Lagerbewegung mit Rückfrage; im Testmodus Trockenlauf.'}
        >
            <button
                type="button"
                className="stkbtn no"
                disabled={gesperrt}
                onClick={() => onPosOut(index)}
            >
                ✕ nicht da
            </button>
        </OeTip>
    );

    if (picked > 0) {
        return (
            <>
                <BestandTooltip
                    werte={werteAusKennzahlen(availOf(row, stock, order))}
                    kopf="Für diesen Auftrag reserviert (Entnahme) — auf dem Lieferschein verfügbar. Diese Menge steht anderen Aufträgen nicht mehr zur Verfügung."
                    quelle="Live aus Weclapp, gerechnet beim Öffnen des Belegs bzw. nach der letzten Buchung."
                >
                    <span className="stkres" tabIndex={0}>
                        🔒 reserviert: {fmt0(picked)}{picked < need ? ' / ' + fmt0(need) : ''}
                    </span>
                </BestandTooltip>
                <OeTip tip={gesperrt ? sperrTip : 'Reservierung für diesen Auftrag wieder aufheben. Der physische Bestand ändert sich NICHT — die Ware ist danach nur nicht mehr für diesen Auftrag gesperrt.'}>
                    <button
                        type="button"
                        className="stkbtn undo"
                        disabled={gesperrt}
                        onClick={() => onUnpick(index)}
                    >
                        ↩ aufheben
                    </button>
                </OeTip>
                {picked < need ? pickBtn : null}
            </>
        );
    }

    const c = availCat(row, stock, order);
    if (c === 3) {
        return null;
    }
    if (c === 2) {
        return lowerBtn;
    }
    if (phys > 0) {
        return <>{pickBtn}{lowerBtn}</>;
    }
    return raiseBtn;
}

/** „geprüft?"-Umschalter einer Position (Original `stockCheckBtn`, 3820). */
export function CheckButton({row, index, stock, order, checked, onToggle}) {
    if (row.locked || row._new || !row.articleId) {
        return <span className="muted" style={{fontSize: 11}}>—</span>;
    }

    const on = checked.includes(checkKeyOf(row));
    const corr = !on && stockCorrectionFor(row, stock, order);
    const tip = on
        ? 'Als ungeprüft markieren.'
        : (corr
            ? 'Als geprüft markieren — die Menge wurde reduziert, beim Abhaken wird die Bestandskorrektur (Warenausgang der Differenz) angeboten.'
            : 'Position als physisch geprüft markieren — auch wenn korrekt nichts da ist. Der Haken liegt im Ordermanagement und ist für alle sichtbar.');

    return (
        <OeTip tip={tip}>
            <button
                type="button"
                className={`chkbtn${on ? ' on' : ''}`}
                aria-pressed={on}
                onClick={() => onToggle(index)}
            >
                {on ? '✓ geprüft' : (corr ? 'geprüft? ±' : 'geprüft?')}
            </button>
        </OeTip>
    );
}

/**
 * Massenaktionen im Spaltenkopf „Verfügbarkeit" (Original `massAvailButtons`,
 * 3690). Sie wirken auf die gerade sichtbaren Lahr-Positionen.
 */
export function MassActions({indices, rows, stock, order, pendingWarehouse, lahrGesperrt, canWarehouse, onMass}) {
    if (!indices.length) {
        return null;
    }

    const zahl = (dir) => indices.filter((i) => massCand(rows[i], dir, stock, order)).length;
    const nRes = zahl('pick');
    const nUnpick = zahl('unpick');
    const nIn = zahl('in');
    const nOut = zahl('out');

    const falschesLager = order.warehouseId
        && String(order.warehouseId) !== WH_LAHR
        && String(pendingWarehouse || '') !== WH_LAHR;

    const btn = (act, cls, txt, cnt, tip, aus) => (
        <OeTip tip={tip}>
            <button
                type="button"
                className={`massbtn ${cls}`}
                disabled={!cnt || aus}
                onClick={() => onMass(act, indices)}
            >
                {txt}{cnt ? <> <b>{cnt}</b></> : null}
            </button>
        </OeTip>
    );

    const gesperrt = lahrGesperrt || !canWarehouse;
    const sperrTip = lahrGesperrt ? SPERRE_TIP : 'Zum Buchen und Reservieren fehlt das Recht „Lager".';

    return (
        <div className="massrow">
            {btn('pick', 'res', '🔒 reservieren', nRes,
                gesperrt
                    ? sperrTip
                    : (falschesLager
                        ? 'Reservieren geht nur, wenn das Auftragslager Lahr ist — erst „alle Lahr" wählen und speichern.'
                        : `Reserviert alle ${nRes} sichtbaren Lahr-Position(en) mit verfügbarem Bestand für diesen Auftrag (Entnahme, höchstens Lagerbestand minus von anderen reserviert). Ändert den Lagerbestand NICHT.`),
                falschesLager || gesperrt)}
            {btn('unpick', 'undo', '↩ aufheben', nUnpick,
                gesperrt
                    ? sperrTip
                    : `Hebt die Reservierung aller ${nUnpick} sichtbaren, bereits reservierten Lahr-Position(en) wieder auf. Ändert den physischen Bestand NICHT.`,
                gesperrt)}
            {btn('in', 'ok', '✓ alle da', nIn,
                gesperrt
                    ? sperrTip
                    : `„Ware ist physisch da": bucht für ${nIn} sichtbare Lahr-Position(en) ohne (volle) Deckung einen Wareneingang, bis sie lieferbar sind. Echte Lagerbewegung — fragt vorher nach.`,
                gesperrt)}
            {btn('out', 'no', '✕ alle weg', nOut,
                gesperrt
                    ? sperrTip
                    : `„Ware ist physisch NICHT da": senkt für ${nOut} sichtbare Lahr-Position(en) den Lagerbestand auf die von anderen Aufträgen reservierte Menge. Echte Lagerbewegung — fragt vorher nach.`,
                gesperrt)}
        </div>
    );
}

/** Sammelhaken „alle Lahr geprüft" im Spaltenkopf (Original 1901). */
export function CheckAllLahrButton({indices, rows, checked, onCheckAll}) {
    if (!indices.length) {
        return <span className="muted" style={{fontSize: 11}}>—</span>;
    }
    const alle = indices.every((i) => checked.includes(checkKeyOf(rows[i])));

    return (
        <OeTip tip="Markiert alle sichtbaren LAHR-Positionen auf einmal als geprüft (nochmal klicken = wieder abwählen). Schneider ist bereits geliefert. Es wird nichts gebucht.">
            <button
                type="button"
                className={`chkbtn${alle ? ' on' : ''}`}
                aria-pressed={alle}
                onClick={() => onCheckAll(indices)}
            >
                {alle ? '✓ alle Lahr geprüft' : 'alle Lahr geprüft'}
            </button>
        </OeTip>
    );
}

export {bookQtyFor};
